import { useEffect, useRef, useState } from "react";
import { useAdminAuth } from "@/contexts/useAdminAuth";
import { connectAdminMessagingSSE } from "@a7y/api-client";

/**
 * Hook that manages an SSE connection for admin messaging unread updates.
 *
 * Same visibility-aware, exponential-backoff reconnection pattern as the
 * webapp's useMessagingSSE hook, but connects to `/admin/messaging/sse`.
 */
export function useAdminMessagingSSE(): {
  unreadThreadCount: number;
  unreadByThread: Record<string, number>;
} {
  const { getToken } = useAdminAuth();
  const [unreadThreadCount, setUnreadThreadCount] = useState(0);
  const [unreadByThread, setUnreadByThread] = useState<Record<string, number>>({});

  const sseDisconnectRef = useRef<(() => void) | null>(null);
  const connectingRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const isOnline = () =>
      typeof navigator !== "undefined" ? navigator.onLine : true;

    const computeBackoffMs = (attempt: number) => {
      const base = Math.pow(2, Math.max(0, attempt - 1));
      const ms = Math.min(30000, 1000 * base);
      const jitter = ms * 0.2 * (Math.random() - 0.5) * 2;
      return Math.max(500, Math.floor(ms + jitter));
    };

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (!isOnline()) return;
      clearReconnectTimeout();
      reconnectAttemptRef.current = Math.min(reconnectAttemptRef.current + 1, 10);
      const delay = computeBackoffMs(reconnectAttemptRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        connectIfNeeded();
      }, delay);
    };

    const applyUnreadPayload = (payload: Record<string, unknown>) => {
      if (payload && typeof payload === "object") {
        if (typeof payload.unreadThreadCount === "number") {
          setUnreadThreadCount(payload.unreadThreadCount);
        }
        if (payload.unreadByThread && typeof payload.unreadByThread === "object") {
          setUnreadByThread(payload.unreadByThread as Record<string, number>);
        }
      }
    };

    const connectIfNeeded = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible")
        return;
      if (!isOnline()) return;
      if (sseDisconnectRef.current) return;
      if (connectingRef.current) return;
      connectingRef.current = true;
      clearReconnectTimeout();

      try {
        const disconnect = connectAdminMessagingSSE({
          getToken,
          onOpen: () => {
            reconnectAttemptRef.current = 0;
            clearReconnectTimeout();
          },
          onInit: (payload: unknown) => {
            applyUnreadPayload(payload as Record<string, unknown>);
          },
          onUpdate: (payload: unknown) => {
            applyUnreadPayload(payload as Record<string, unknown>);
          },
          onPing: () => {
            // keepalive
          },
          onError: () => {
            if (sseDisconnectRef.current) {
              try {
                sseDisconnectRef.current();
              } catch {
                // ignore
              }
              sseDisconnectRef.current = null;
            }
            scheduleReconnect();
          },
          onReconnect: () => {
            // Server-initiated graceful reconnect (max lifetime) — reconnect immediately.
            // A7Y-5844: release the ended stream first; with the ref still set,
            // `connectIfNeeded` returned early and the stream stayed closed.
            if (sseDisconnectRef.current) {
              try {
                sseDisconnectRef.current();
              } catch {
                // ignore
              }
              sseDisconnectRef.current = null;
            }
            reconnectAttemptRef.current = 0;
            connectIfNeeded();
          },
        });
        sseDisconnectRef.current = disconnect;
      } catch {
        scheduleReconnect();
      } finally {
        connectingRef.current = false;
      }
    };

    const disconnectIfNeeded = () => {
      clearReconnectTimeout();
      if (sseDisconnectRef.current) {
        try {
          sseDisconnectRef.current();
        } catch {
          // ignore
        }
        sseDisconnectRef.current = null;
      }
    };

    if (typeof document !== "undefined") {
      if (document.visibilityState === "visible") {
        connectIfNeeded();
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectIfNeeded();
      } else if (document.visibilityState === "visible") {
        connectIfNeeded();
      }
    };

    const handleOnline = () => connectIfNeeded();
    const handleOffline = () => disconnectIfNeeded();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      disconnectIfNeeded();
    };
  }, [getToken]);

  return { unreadThreadCount, unreadByThread };
}
